import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Car, Calendar, DollarSign, ShieldCheck, ArrowRight, CheckCircle2, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  pending_signature: { label: "Awaiting signature", cls: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" },
  pending_deposit: { label: "Awaiting deposit", cls: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" },
  active: { label: "Active", cls: "bg-cream/10 border-cream/30 text-cream" },
  return_requested: { label: "Return requested", cls: "bg-blue-500/10 border-blue-500/30 text-blue-400" },
  completed: { label: "Completed", cls: "bg-white/5 border-white/10 text-white/50" },
  cancelled: { label: "Cancelled", cls: "bg-white/5 border-white/10 text-white/40" },
};

export default function DriverRentalsDashboard() {
  const [params] = useSearchParams();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    api.get("/driver-rentals/me")
      .then((r) => setRentals(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    // Confirm recent Stripe checkout
    const paid = params.get("paid");
    const sessionId = params.get("session_id");
    if (paid && sessionId) {
      api.get(`/payments/status/${sessionId}`).then(() => {
        toast.success("Payment confirmed");
        setTimeout(load, 800);
      });
    }
  }, [params]);

  const active = rentals.find((r) => ["active", "pending_deposit", "pending_signature", "return_requested"].includes(r.status));

  const payWeek = async (rental) => {
    try {
      const { data } = await api.post(`/driver-rentals/${rental.id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleAutopay = async (rental) => {
    try {
      await api.post(`/driver-rentals/${rental.id}/autopay`, { enabled: !rental.autopay_enabled });
      toast.success(`AutoPay ${!rental.autopay_enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const requestReturn = async (rental) => {
    const mileage = window.prompt("Enter current mileage (miles):");
    if (!mileage) return;
    try {
      await api.post(`/driver-rentals/${rental.id}/return-request`, {
        mileage_end: Number(mileage), notes: "",
      });
      toast.success("Return requested — dispatch will contact you.");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="h-96 flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-2 border-cream border-t-transparent animate-spin" />
  </div>;

  if (!active) {
    return (
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-12 text-center">
        <Car size={48} className="text-cream mx-auto mb-4" />
        <h1 className="font-serif text-3xl sm:text-4xl mb-3">No active rental</h1>
        <p className="text-white/60 mb-6">Ready to start driving? Pick a vehicle from our rideshare-approved fleet.</p>
        <Link to="/driver-rentals" className="btn-cream px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
          Browse rentals <ArrowRight size={14} />
        </Link>
      </section>
    );
  }

  const meta = STATUS_META[active.status] || STATUS_META.active;
  const weeklyDue = active.weekly_rate;
  const nextPaymentAt = active.last_paid_at
    ? new Date(new Date(active.last_paid_at).getTime() + 7 * 24 * 3600 * 1000)
    : new Date();

  return (
    <section data-testid="dr-dashboard-page" className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
      <div className="label-caps text-cream mb-2">Driver rental dashboard</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-2">{user?.name?.split(" ")[0]}, welcome back.</h1>
      <p className="text-white/60 mb-10">Manage your rental, pay your week, and request a return.</p>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="glass rounded-3xl overflow-hidden">
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`label-caps px-2.5 py-1 rounded-full border ${meta.cls}`}>{meta.label}</span>
              {active.autopay_enabled && (
                <span className="label-caps px-2.5 py-1 rounded-full bg-cream/10 border border-cream/30 text-cream">AutoPay ON</span>
              )}
            </div>
            <h2 className="font-serif text-3xl">{active.vehicle_name}</h2>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <Stat icon={DollarSign} label="Weekly rate" value={`$${active.weekly_rate.toFixed(2)}`} />
              <Stat icon={ShieldCheck} label="Deposit held" value={`$${active.deposit.toFixed(2)}`} />
              <Stat icon={Calendar} label="Weeks paid" value={active.weeks_paid} />
              <Stat icon={Calendar} label="Term ends" value={new Date(active.expected_return_at).toLocaleDateString()} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass rounded-3xl p-6">
            <div className="label-caps text-cream mb-2">Next payment</div>
            <div className="font-serif text-3xl mb-1">${weeklyDue.toFixed(2)}</div>
            <div className="text-sm text-white/60 mb-5">
              Due {nextPaymentAt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </div>
            {active.status === "pending_signature" && (
              <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-3 text-sm text-yellow-400 mb-4">
                Your agreement is unsigned. Return to the application to sign.
              </div>
            )}
            <button data-testid="dr-pay-week" onClick={() => payWeek(active)}
              disabled={active.status === "return_requested" || active.status === "completed"}
              className="btn-cream w-full py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <DollarSign size={14} /> Pay this week
            </button>
            <button data-testid="dr-toggle-autopay" onClick={() => toggleAutopay(active)}
              className="w-full mt-2 px-4 py-2 rounded-full text-xs border border-white/10 hover:border-cream/50 hover:text-white text-white/60">
              {active.autopay_enabled ? "Disable AutoPay" : "Enable weekly AutoPay"}
            </button>
          </div>

          <div className="glass rounded-3xl p-6">
            <div className="label-caps text-cream mb-3">Manage</div>
            <button data-testid="dr-request-return" onClick={() => requestReturn(active)}
              disabled={active.return_requested}
              className="w-full px-4 py-2.5 rounded-full text-sm border border-white/10 hover:border-white/30 mb-2 inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <Wrench size={14} />
              {active.return_requested ? "Return already requested" : "Request return / end rental"}
            </button>
            <div className="text-xs text-white/40 mt-2 leading-relaxed">
              Dispatch will contact you within 24 hours to schedule your return and refund your deposit.
            </div>
          </div>
        </div>
      </div>

      {active.payment_history && active.payment_history.length > 0 && (
        <div className="glass rounded-3xl p-6 mt-6" data-testid="dr-payments">
          <div className="label-caps text-cream mb-4">Payment history</div>
          <div className="space-y-2">
            {active.payment_history.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-cream" />
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <span className="text-cream font-serif">${p.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1 text-white/50 mb-1">
      <Icon size={12} className="text-cream" />
      <span className="label-caps text-[10px]">{label}</span>
    </div>
    <div className="font-serif text-xl">{value}</div>
  </div>
);
